import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api.js'
import { useAuth } from '../contexts/AuthContext.jsx'
import TimeOffModal from '../components/TimeOffModal.jsx'
import CalendarDayModal from '../components/CalendarDayModal.jsx'
import Icon from '../components/Icon.jsx'
import { MultiSelectDropdown } from '../components/Dropdown.jsx'
import { DateRangeFilter } from '../components/DatePicker.jsx'
import { monthDays, weekdayLabels } from '../components/Calendar.jsx'
import { useToast, useUndo } from '../components/Toast.jsx'
import { toggleInSet, toCsv, fromCsv } from '../lib/sets.js'
import { directoryPeople, onTab, readTab, roleSummary, timeOffModeFor } from '../lib/directory.js'
import {
  addDays,
  addMonths,
  firstOfMonth,
  formatDate,
  formatMonth,
  isIsoDate,
  lastOfMonth,
  monthOf,
  timeWindow,
  windowLabel,
} from '../lib/dates.js'
import { reasonLabel } from '../../../shared/timeoff.js'

/**
 * Who works here — the standing picture, as opposed to the board's picture of one day.
 *
 * This was a wall of read-only crew cards: it could tell you Marek was on BLAIR and knew
 * drywall, and then you had to go somewhere else to change any of it — phone numbers and
 * job types under Settings, absences through a button that opened a dialog per person. The
 * three things anyone actually comes here to change were in three different places, and
 * none of them was this page.
 *
 * So: a directory on the left, one person open on the right, and their phone, their job
 * types and their time off all editable where you are already looking. Crew membership is
 * NOT editable here — that is a different question with its own screen (Settings → Teams for
 * PM crews, Group setup for the others), and it is the one thing on this page that changes
 * who can see what.
 *
 * Everybody who works here, on four tabs (lib/directory.js): All, the default; the crew as
 * each of the two boards has them; and the PMs and PMAs. The two crew tabs are one toggle
 * rather than following whatever mode is live, because the question they answer ("which
 * crew is Marek on in Group mode?") is most often asked while the OTHER mode is running — a
 * week before a switch, or a week after one. On All and on the PMs tab the crews shown are
 * the live board's.
 */
export default function People() {
  const { tenant, can, modeName, enabledModes, roleLabel } = useAuth()
  // A mode the company has turned off is not a tab to offer.
  const usableModes = enabledModes()
  const qc = useQueryClient()
  const toast = useToast()

  // What the page is showing lives in the URL, the way the board's does: which view, which
  // mode, the month, the search, every filter and the person open on the right. A filtered
  // directory is then a link — "here are the glaziers free next week" — and Back returns to
  // what was being looked at rather than to a blank page. Read once, on the way in, and
  // validated rather than trusted: this arrives from an address somebody may have typed.
  // The keys follow the house rule for URLs: `q` for the search, comma-joined lists, one
  // flat key per fact. Only what differs from the default is written, so a plain visit is
  // still /people.
  const [searchParams, setSearchParams] = useSearchParams()
  const isMonth = (s) => /^\d{4}-\d{2}$/.test(s || '')
  const readAbsence = () => {
    for (const dir of ['away', 'free']) {
      const [from, to] = (searchParams.get(dir) || '').split(',')
      if (isIsoDate(from)) return { dir, from, to: isIsoDate(to) ? to : from }
    }
    return null
  }

  const [view, setView] = useState(() => (searchParams.get('view') === 'calendar' ? 'calendar' : 'directory'))
  const [tab, setTab] = useState(() => readTab(searchParams))
  const [month, setMonth] = useState(() => (isMonth(searchParams.get('month')) ? searchParams.get('month') : null))
  const [selectedId, setSelectedId] = useState(() => searchParams.get('person') || null)
  // The calendar day whose dialog is open, as 'YYYY-MM-DD'.
  const [dayOpen, setDayOpen] = useState(null)
  const [query, setQuery] = useState(() => searchParams.get('q') || '')
  const [crewFilter, setCrewFilter] = useState(() => new Set(fromCsv(searchParams.get('crew'))))
  const [jobFilter, setJobFilter] = useState(() => new Set(fromCsv(searchParams.get('job'))))
  // Away and Available are one filter with two directions, not two filters: a person is
  // either free across the chosen days or they are not, so holding both would always mean
  // an empty list. One piece of state enforces that — picking either replaces the other.
  const [absence, setAbsence] = useState(readAbsence) // { dir: 'away' | 'free', from, to } | null
  // Only people with time off whose request has not gone in (migration 025). Offered only to
  // whoever schedules — the payload carries no request status for anyone else.
  const [awaitingOnly, setAwaitingOnly] = useState(() => searchParams.get('awaiting') === '1')
  const [timeOffFor, setTimeOffFor] = useState(null) // { staffId, entry } — add or edit

  // The one effect that writes the URL, built fresh each time so nothing stale lingers.
  // `replace`, not `push`: every keystroke in the search box would otherwise be a history
  // entry, and Back would have to walk through a word one letter at a time.
  useEffect(() => {
    const next = new URLSearchParams()
    if (view === 'calendar') next.set('view', 'calendar')
    if (tab !== 'all') next.set('tab', tab)
    if (month) next.set('month', month)
    if (query) next.set('q', query)
    const crew = toCsv(crewFilter)
    if (crew) next.set('crew', crew)
    const job = toCsv(jobFilter)
    if (job) next.set('job', job)
    if (absence) next.set(absence.dir, `${absence.from},${absence.to}`)
    if (awaitingOnly) next.set('awaiting', '1')
    if (selectedId) next.set('person', selectedId)
    setSearchParams(next, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, tab, month, query, crewFilter, jobFilter, absence, awaitingOnly, selectedId])
  // Held as state rather than read off `data`, because the window below is an input to the
  // very query that produces it. Set once from the first payload and steady after that.
  const [serverToday, setServerToday] = useState(null)

  // What /api/people covers when asked for nothing — mirrored from the route so the union
  // below can tell whether a filter reaches outside it.
  const defaultWindow = useMemo(
    () => (serverToday ? { from: addDays(serverToday, -31), to: addDays(serverToday, 120) } : null),
    [serverToday],
  )

  // The calendar asks for its own month; the directory takes the default window. Keeping the
  // range in the key means switching months refetches rather than showing last month's
  // absences under this month's heading.
  //
  // The directory takes the default window UNTIL a date filter reaches outside it. The
  // payload only carries time off inside the window it was asked for, so a filter set to
  // next March against a window ending in January would quietly match nobody and read as
  // "everyone is free" — the most dangerous wrong answer this page can give.
  const range = useMemo(() => {
    if (month) return { from: firstOfMonth(month), to: lastOfMonth(month) }
    if (!absence || !defaultWindow) return null
    return {
      from: absence.from < defaultWindow.from ? absence.from : defaultWindow.from,
      to: absence.to > defaultWindow.to ? absence.to : defaultWindow.to,
    }
  }, [month, absence, defaultWindow])

  const { data, isLoading } = useQuery({
    queryKey: ['people', range?.from, range?.to],
    queryFn: () => api.get(range ? `/api/people?from=${range.from}&to=${range.to}` : '/api/people'),
  })

  useEffect(() => {
    if (data?.today) setServerToday(data.today)
  }, [data?.today])

  // A crew tab for a mode the company has since turned off — the tab that would have moved
  // you off it has just disappeared — falls back to All rather than to an empty view.
  const crewTab = tab === 'normal' || tab === 'cat'
  const activeTab = crewTab && !usableModes.includes(tab) ? 'all' : tab
  // Whose crews to show: the tab's own on a crew tab, otherwise whatever board is running.
  const wantedMode = activeTab === 'normal' || activeTab === 'cat' ? activeTab : data?.currentMode ?? 'normal'
  const activeMode = usableModes.includes(wantedMode) ? wantedMode : usableModes[0] || 'normal'
  const activeMonth = month ?? (data?.today ? monthOf(data.today) : null)

  // Everybody the server sent, and the people on the tab being looked at. `people` is what a
  // name or an id is looked up in; `staff` is what the list, the count and the calendar show.
  const people = useMemo(() => directoryPeople(data), [data])
  const staff = useMemo(() => people.filter((p) => onTab(p, activeTab)), [people, activeTab])
  const groups = useMemo(() => data?.groups?.[activeMode] || [], [data, activeMode])
  const jobTypes = data?.jobTypes || []

  const staffById = useMemo(() => new Map(people.map((s) => [s.id, s])), [people])
  const tabIds = useMemo(() => new Set(staff.map((s) => s.id)), [staff])
  // Crews and job types are the crew's. On the PMs & PMAs tab neither filter means anything,
  // so they are shown disabled and not applied, and whatever they were set to is kept for
  // coming back.
  const crewFiltersApply = activeTab !== 'office'
  /** Crew name for the crew; what they do for everyone else, and for everyone on the PMs tab. */
  const affiliation = (p) =>
    p.fieldStaff && activeTab !== 'office' ? crewOf.get(p.id)?.name || 'Not on a crew' : roleSummary(p, roleLabel)
  const jobTypeById = useMemo(() => new Map(jobTypes.map((j) => [j.id, j])), [jobTypes])

  /** staffId → the crew they are on in the mode being viewed. */
  const crewOf = useMemo(() => {
    const m = new Map()
    for (const g of groups) for (const id of g.staffIds) if (!m.has(id)) m.set(id, g)
    return m
  }, [groups])

  const offByStaff = useMemo(() => {
    const m = new Map()
    for (const t of data?.timeOff || []) {
      if (!m.has(t.staffId)) m.set(t.staffId, [])
      m.get(t.staffId).push(t)
    }
    for (const list of m.values()) list.sort((a, b) => (a.from < b.from ? -1 : 1))
    return m
  }, [data?.timeOff])

  const awayToday = (id) =>
    (offByStaff.get(id) || []).some((t) => t.from <= data.today && data.today <= t.to)

  /** Any time off that overlaps [from, to] at all — an overlap test, not containment. */
  const awayBetween = (id, from, to) =>
    (offByStaff.get(id) || []).some((t) => t.from <= to && t.to >= from)

  const needle = query.trim().toLowerCase()
  const shown = useMemo(
    () =>
      staff.filter((s) => {
        if (
          needle &&
          !s.name.toLowerCase().includes(needle) &&
          !(s.title || '').toLowerCase().includes(needle) &&
          !(s.phone || '').includes(needle)
        ) {
          return false
        }
        // A crew filter asks about the crew: "Not on a crew" is a crew member without one, not
        // everybody in the office, who is on no crew by definition.
        if (crewFiltersApply && crewFilter.size && (!s.fieldStaff || !crewFilter.has(crewOf.get(s.id)?.id || '_none'))) {
          return false
        }
        if (crewFiltersApply && jobFilter.size && !(s.jobTypeIds || []).some((j) => jobFilter.has(j))) return false
        if (absence) {
          // Exact complements over the same window: away is "off at any point in it",
          // available is "off at no point in it", which is the same as free for the whole
          // of it. Somebody off the Monday of a Mon–Wed job is away for it and not
          // available for it, which is the answer a scheduler wants both times.
          const away = awayBetween(s.id, absence.from, absence.to)
          if (absence.dir === 'away' ? !away : away) return false
        }
        if (awaitingOnly && !(offByStaff.get(s.id) || []).some((t) => t.requestStatus === 'awaiting')) return false
        return true
      }),
    // awayBetween reads offByStaff, which moves only when the query refetches — which also
    // replaces `staff`, so it is covered.
    [staff, needle, crewFilter, jobFilter, absence, awaitingOnly, crewOf, offByStaff, crewFiltersApply],
  )

  const filtered =
    Boolean(needle) ||
    (crewFiltersApply && (crewFilter.size > 0 || jobFilter.size > 0)) ||
    Boolean(absence) ||
    awaitingOnly
  const clearFilters = () => {
    setQuery('')
    setCrewFilter(new Set())
    setJobFilter(new Set())
    setAbsence(null)
    setAwaitingOnly(false)
  }

  // Keep a selection that is still on screen. Landing on the page, or filtering the selected
  // person out of the list, should leave somebody open rather than an empty right-hand pane.
  useEffect(() => {
    if (!shown.length) return
    if (!selectedId || !shown.some((s) => s.id === selectedId)) setSelectedId(shown[0].id)
  }, [shown, selectedId])

  // Only somebody on the list being shown. A tab or a filter that leaves nobody leaves the pane
  // empty, rather than still showing whoever was open on the last tab. Falling back to the
  // first person here, as the effect above is about to, keeps a tab switch from flashing
  // "Nobody to show" for the one paint before the effect runs.
  const selected = shown.find((s) => s.id === selectedId) || shown[0] || null
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['people'] })
    // Time off is drawn on the board too, and both edits below touch it.
    qc.invalidateQueries({ queryKey: ['schedule'] })
  }
  const undo = useUndo(invalidate)

  const canEditPeople = Boolean(can?.administer)
  const canAddTimeOff = Boolean(data?.can?.addTimeOff)
  const timeOffMode = timeOffModeFor(data?.can?.writeBoard, activeMode)

  async function savePhone(id, phone) {
    await api.patch(`/api/admin/users/${id}`, { phone: phone.trim() || null })
    invalidate()
  }

  async function saveTitle(id, title) {
    await api.patch(`/api/admin/users/${id}`, { title: title.trim() || null })
    invalidate()
  }

  async function saveJobTypes(id, jobTypeIds) {
    await api.put(`/api/admin/users/${id}/job-types`, { jobTypeIds })
    invalidate()
  }

  // The dialog's whole answer, passed through, as on the board.
  async function saveTimeOff(fields) {
    const { staffId, entry } = timeOffFor
    try {
      if (entry) {
        await api.patch(`/api/time-off/${entry.id}`, { ...fields, mode: timeOffMode })
      } else {
        await api.post('/api/time-off', { ...fields, userId: staffId, mode: timeOffMode })
      }
      setTimeOffFor(null)
      invalidate()
      toast.success(entry ? 'Time off updated.' : 'Time off added.')
    } catch (err) {
      toast.error(err)
    }
  }

  async function removeTimeOff(id) {
    // The row as this page holds it, read before it goes: it is what Undo writes back.
    const t = (data?.timeOff || []).find((x) => x.id === id)
    try {
      await api.del(`/api/time-off/${id}?mode=${timeOffMode}`)
      setTimeOffFor(null)
      invalidate()
      if (!t) {
        toast.success('Time off removed.')
        return
      }
      undo('Time off removed.', () =>
        api.post('/api/time-off', {
          userId: t.staffId,
          fromDate: t.from,
          toDate: t.to,
          note: t.note,
          startTime: t.startTime,
          endTime: t.endTime,
          reason: t.reason,
          requestStatus: t.requestStatus,
          internalNote: t.internalNote,
          mode: timeOffMode,
        }),
      )
    } catch (err) {
      toast.error(err)
    }
  }

  if (isLoading || !data) return <div className="page empty">Loading people…</div>

  return (
    <div className="page people-page">
      <div className="page-head no-print">
        <div>
          <h1>Directory</h1>
        </div>

        <div className="spacer" />

        {/* Who is listed. The two crew tabs are named for the company's own modes and only
            offered for a mode it uses. */}
        <div className="segmented">
          <button className={activeTab === 'all' ? 'selected' : ''} onClick={() => setTab('all')}>
            All
          </button>
          {usableModes.map((m) => (
            <button key={m} className={activeTab === m ? 'selected' : ''} onClick={() => setTab(m)}>
              {modeName(m)}
            </button>
          ))}
          <button className={activeTab === 'office' ? 'selected' : ''} onClick={() => setTab('office')}>
            PMs &amp; PMAs
          </button>
        </div>

        <div className="segmented">
          <button className={view === 'directory' ? 'selected' : ''} onClick={() => setView('directory')}>
            Directory
          </button>
          <button className={view === 'calendar' ? 'selected' : ''} onClick={() => setView('calendar')}>
            Time off calendar
          </button>
        </div>
      </div>

      {view === 'calendar' ? (
        <CalendarView
          month={activeMonth}
          setMonth={setMonth}
          weekStart={tenant?.weekStart || 1}
          // The time off of the people on the tab, so the calendar and the list agree on who.
          timeOff={(data.timeOff || []).filter((t) => tabIds.has(t.staffId))}
          holidays={data.holidays || {}}
          staffById={staffById}
          today={data.today}
          onPickDay={setDayOpen}
        />
      ) : (
        <>
          {/* Above the panes, not inside the list. A dropdown opened from within the list was
              clipped by the overflow that makes it scroll — on a short window the panel was
              cut off mid-option — and it covered the names it was meant to be filtering. */}
          <div className="dir-toolbar card">
            <div className="filter-panel-search dir-search">
              <Icon name="search" size={13} />
              <input
                type="text"
                value={query}
                placeholder="Search name, title or phone…"
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
            <MultiSelectDropdown
              value={crewFilter}
              onToggle={(id) => setCrewFilter(toggleInSet(crewFilter, id))}
              onClear={() => setCrewFilter(new Set())}
              options={[
                ...groups.map((g) => ({ value: g.id || '_none', label: g.name })),
                { value: '_none', label: 'Not on a crew' },
              ].filter(unique)}
              placeholder="Crew"
              summary
              searchable
              disabled={!crewFiltersApply}
              title={crewFiltersApply ? undefined : 'PMs and PMAs are not on crews'}
            />
            <MultiSelectDropdown
              value={jobFilter}
              onToggle={(id) => setJobFilter(toggleInSet(jobFilter, id))}
              onClear={() => setJobFilter(new Set())}
              options={jobTypes.map((j) => ({ value: j.id, label: j.name }))}
              placeholder="Job type"
              summary
              searchable
              disabled={!crewFiltersApply}
              title={crewFiltersApply ? undefined : 'Job types are what the crew are signed off to do'}
            />
            {/* Two directions on one filter. Choosing either clears the other, so the pair
                can never be set to a combination that matches nobody by construction. */}
            <DateRangeFilter
              label="Away"
              title="Only people off on a day, or at some point across a range"
              value={absence?.dir === 'away' ? absence : null}
              onChange={(v) => setAbsence(v && { dir: 'away', ...v })}
              weekStart={tenant?.weekStart || 1}
              today={data?.today}
            />
            <DateRangeFilter
              label="Available"
              title="Only people free for every day of it"
              value={absence?.dir === 'free' ? absence : null}
              onChange={(v) => setAbsence(v && { dir: 'free', ...v })}
              weekStart={tenant?.weekStart || 1}
              today={data?.today}
            />
            {canAddTimeOff && (
              <button
                type="button"
                className={`btn sm filter-trigger ${awaitingOnly ? 'active' : ''}`}
                aria-pressed={awaitingOnly}
                title="Only people with time off whose request has not been submitted yet"
                onClick={() => setAwaitingOnly((v) => !v)}
              >
                Request outstanding
              </button>
            )}
            {filtered && (
              <button type="button" className="btn ghost sm" onClick={clearFilters}>
                Clear
              </button>
            )}
            <span className="spacer" />
            <span className="hint">{shown.length} of {staff.length}</span>
          </div>

          <div className="dir">
          <div className="dir-list">
            <ul className="dir-people">
              {shown.map((s) => (
                <li key={s.id}>
                  <button
                    type="button"
                    className={`dir-person ${s.id === selectedId ? 'on' : ''}`}
                    onClick={() => setSelectedId(s.id)}
                  >
                    <span className="dir-person-main">
                      <span className="dir-person-name">{s.name}</span>
                      {awayToday(s.id) && <span className="tag muted">Away</span>}
                    </span>
                    <span className="dir-person-sub">
                      {[s.title, affiliation(s), s.phone].filter(Boolean).join(' · ')}
                    </span>
                  </button>
                </li>
              ))}
              {shown.length === 0 && <li className="empty">Nobody matches.</li>}
            </ul>
          </div>

          {selected ? (
            <PersonPane
              key={selected.id}
              person={selected}
              affiliation={affiliation(selected)}
              jobTypes={jobTypes}
              jobTypeById={jobTypeById}
              timeOff={offByStaff.get(selected.id) || []}
              today={data.today}
              canEdit={canEditPeople}
              canAddTimeOff={canAddTimeOff}
              onSavePhone={savePhone}
              onSaveTitle={saveTitle}
              onSaveJobTypes={saveJobTypes}
              onAddTimeOff={() => setTimeOffFor({ staffId: selected.id, entry: null })}
              onEditTimeOff={(entry) => setTimeOffFor({ staffId: selected.id, entry })}
              onRemoveTimeOff={removeTimeOff}
            />
          ) : (
            <div className="card empty dir-detail">Nobody to show.</div>
          )}
          </div>
        </>
      )}

      {/* The day dialog and the entry dialog are one flow: picking a row in the first opens
          the second over it, and closing that comes back to the day rather than to the
          month, which is where the next change usually is. */}
      {dayOpen && (
        <CalendarDayModal
          date={dayOpen}
          // The tab's people, as the calendar cell it opened from shows them.
          entries={(data.timeOff || []).filter((t) => tabIds.has(t.staffId) && t.from <= dayOpen && dayOpen <= t.to)}
          staffById={staffById}
          holiday={data.holidays?.[dayOpen] || null}
          mode={activeMode}
          canEdit={canAddTimeOff}
          canSetHoliday={Boolean(data.can?.setHoliday?.[activeMode])}
          onEdit={(t) => setTimeOffFor({ staffId: t.staffId, entry: t })}
          onHolidayChanged={invalidate}
          onClose={() => setDayOpen(null)}
        />
      )}

      {timeOffFor && (
        <TimeOffModal
          staff={staffById.get(timeOffFor.staffId)}
          date={data.today}
          weekStart={tenant?.weekStart || 1}
          editing={timeOffFor.entry}
          existing={timeOffFor.entry ? [] : offByStaff.get(timeOffFor.staffId) || []}
          onSave={saveTimeOff}
          onDelete={removeTimeOff}
          onClose={() => setTimeOffFor(null)}
        />
      )}
    </div>
  )
}

const unique = (o, i, all) => all.findIndex((x) => x.value === o.value) === i

/**
 * One value, edited where it is shown: saved when the field is left or Enter is pressed, with
 * a Save button while something is unsaved. A refusal puts the saved value back, so the field
 * never goes on showing something the server did not take. The phone number and the title
 * are both this, and were two copies of it for as long as it took to add the second.
 */
function InlineField({ value, onSave, savedMessage, ...input }) {
  const toast = useToast()
  const [draft, setDraft] = useState(value || '')
  const [saving, setSaving] = useState(false)
  const dirty = draft.trim() !== (value || '')

  async function commit() {
    if (!dirty || saving) return
    setSaving(true)
    try {
      await onSave(draft)
      toast.success(savedMessage)
    } catch (err) {
      toast.error(err)
      setDraft(value || '')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="pp-inline">
      <input
        {...input}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur() } }}
      />
      {dirty && (
        <button type="button" className="btn sm primary" onClick={commit} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </button>
      )}
    </div>
  )
}

/**
 * One person, open for editing.
 *
 * Keyed by person id at the call site, so switching people remounts this rather than leaving
 * a half-typed phone number sitting in a field now labelled with somebody else's name.
 */
function PersonPane({
  person,
  affiliation,
  jobTypes,
  jobTypeById,
  timeOff,
  today,
  canEdit,
  canAddTimeOff,
  onSavePhone,
  onSaveTitle,
  onSaveJobTypes,
  onAddTimeOff,
  onEditTimeOff,
  onRemoveTimeOff,
}) {
  const toast = useToast()
  // Job types write through on each click, so the list has to be local as well as saved —
  // waiting for a refetch to tick a chip makes six changes feel like six page loads.
  const [jobIds, setJobIds] = useState(person.jobTypeIds || [])
  const [savingJobs, setSavingJobs] = useState(false)

  async function toggleJob(id) {
    const next = jobIds.includes(id) ? jobIds.filter((x) => x !== id) : [...jobIds, id]
    const previous = jobIds
    setJobIds(next) // optimistic: the chip answers the click, not the round trip
    setSavingJobs(true)
    try {
      await onSaveJobTypes(person.id, next)
    } catch (err) {
      setJobIds(previous)
      toast.error(err)
    } finally {
      setSavingJobs(false)
    }
  }

  const active = jobTypes.filter((j) => j.active !== false)
  const upcoming = timeOff.filter((t) => t.to >= today)
  const past = timeOff.filter((t) => t.to < today)

  return (
    <div className="card pp">
      <header className="pp-head">
        <div>
          <h2>{person.name}</h2>
          <p className="sub">{affiliation}</p>
        </div>
        {/* The rest of this person — sign-in, roles, enabling and disabling — lives in User
            Management. A new tab rather than a navigation, so the Directory, its filters
            and the person open in it are all still here afterwards. Offered to the people
            that page opens for; for anyone else it would be a link to a redirect. */}
        {canEdit && (
          <a
            className="btn ghost sm pp-head-link"
            href={`/settings/user-management?user=${person.id}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            User management
            <Icon name="external" size={13} />
          </a>
        )}
      </header>

      <section className="pp-section">
        <h3 className="pp-label">Title</h3>
        {canEdit ? (
          <InlineField
            type="text"
            value={person.title}
            placeholder="e.g. Foreman"
            maxLength={60}
            savedMessage="Title saved."
            onSave={(title) => onSaveTitle(person.id, title)}
          />
        ) : (
          <p className="pp-value">{person.title || <span className="hint">None</span>}</p>
        )}
      </section>

      <section className="pp-section">
        <h3 className="pp-label">Phone</h3>
        {canEdit ? (
          <InlineField
            type="tel"
            value={person.phone}
            placeholder="Enter phone number"
            savedMessage="Phone number saved."
            onSave={(phone) => onSavePhone(person.id, phone)}
          />
        ) : (
          <p className="pp-value">{person.phone || <span className="hint">None on file</span>}</p>
        )}
      </section>

      {/* The crew's alone: what somebody is signed off to do on a job. For a PM or an owner
          it is not a question anybody asks, so it is left out rather than shown empty. */}
      {person.fieldStaff && (
      <section className="pp-section">
        <h3 className="pp-label">
          Job types
          {canEdit && <span className="hint">Click to add or remove</span>}
        </h3>
        {canEdit ? (
          <div className={`chip-select ${savingJobs ? 'saving' : ''}`}>
            {active.map((j) => (
              <button
                key={j.id}
                type="button"
                className={`chip-option ${jobIds.includes(j.id) ? 'on' : ''}`}
                onClick={() => toggleJob(j.id)}
              >
                {j.name}
              </button>
            ))}
            {active.length === 0 && (
              <span className="hint">No job types yet — add some under Settings → Job types.</span>
            )}
          </div>
        ) : (
          <div className="people-skills">
            {jobIds.map((id) => (
              <span key={id} className="skill-chip">{jobTypeById.get(id)?.name || '—'}</span>
            ))}
            {jobIds.length === 0 && <span className="hint">Nothing signed off yet.</span>}
          </div>
        )}
      </section>
      )}

      <section className="pp-section">
        <h3 className="pp-label">
          Time off
          {past.length > 0 && <span className="hint">{past.length} earlier this period</span>}
        </h3>

        {upcoming.length > 0 ? (
          <ul className="pp-timeoff">
            {upcoming.map((t) => (
              <TimeOffRow
                key={t.id}
                entry={t}
                today={today}
                canEdit={canAddTimeOff}
                onEdit={() => onEditTimeOff(t)}
                onRemove={() => onRemoveTimeOff(t.id)}
              />
            ))}
          </ul>
        ) : (
          <p className="hint pp-value">Nothing booked.</p>
        )}

        {canAddTimeOff && (
          <button type="button" className="btn ghost sm pp-add" onClick={onAddTimeOff}>
            <Icon name="plus" size={13} /> Add time off
          </button>
        )}
      </section>
    </div>
  )
}

function TimeOffRow({ entry, today, canEdit, onEdit, onRemove }) {
  const current = entry.from <= today && today <= entry.to
  return (
    <li className={`pp-timeoff-row ${current ? 'now' : ''}`}>
      <span className="pp-timeoff-when">
        {formatDate(entry.from)}
        {entry.to !== entry.from ? ` – ${formatDate(entry.to)}` : ''}
      </span>
      {/* The hours read as their own fact, not folded into the note: the note is what the
          person said, the hours are what the board does with it. */}
      {timeWindow(entry) && <span className="pp-timeoff-hours">{timeWindow(entry)}</span>}
      {entry.note && <span className="pp-timeoff-note">{entry.note}</span>}
      {/* Present only in a scheduler's payload, so these need no gate of their own here. */}
      {entry.reason && <span className="pp-timeoff-private">{reasonLabel(entry.reason)}</span>}
      {entry.requestStatus === 'awaiting' && <span className="tag awaiting">Request outstanding</span>}
      {entry.internalNote && <span className="pp-timeoff-private" title={entry.internalNote}>Note</span>}
      {current && <span className="pp-timeoff-now">Off now</span>}
      {canEdit && (
        <span className="pp-timeoff-actions">
          <button type="button" className="icon-btn" title="Change" onClick={onEdit}>
            <Icon name="edit" size={14} />
          </button>
          <button type="button" className="icon-btn" title="Remove" onClick={onRemove}>
            <Icon name="trash" size={14} />
          </button>
        </span>
      )}
    </li>
  )
}


/** Everybody's time off for a month, so a scheduler can see a thin Friday coming. */
function CalendarView({ month, setMonth, weekStart, timeOff, holidays, staffById, today, onPickDay }) {
  if (!month) return <div className="empty">Loading…</div>

  // The same day-classing the board month view and the picker's calendar use.
  const days = monthDays(month, weekStart, today)
  const labels = weekdayLabels(month, weekStart)
  const offOn = (d) => timeOff.filter((t) => t.from <= d && d <= t.to)

  return (
    <div className="card cal-card">
      <div className="cal-head">
        <button className="btn sm" onClick={() => setMonth(addMonths(month, -1))} aria-label="Previous month">‹</button>
        <h2>{formatMonth(month)}</h2>
        <button className="btn sm" onClick={() => setMonth(addMonths(month, 1))} aria-label="Next month">›</button>
        <div className="spacer" />
        <button className="btn sm" onClick={() => setMonth(monthOf(today))}>This month</button>
      </div>

      <div className="cal-grid">
        {labels.map((l) => (
          <div key={l} className="cal-label">{l}</div>
        ))}
        {days.map(({ date: d, outside, weekend, isToday }) => {
          const off = offOn(d)
          // The window the payload covers is a week wider than the month at both ends, so
          // the neighbouring days this grid draws are marked too.
          const holiday = holidays[d]
          // A day with nothing on it has nothing to open. The chips inside are plain text
          // rather than buttons for the same reason the cell is one: a name at 0.72rem in
          // a seven-column month is not a target, and nesting a button in a button is not
          // a thing HTML allows anyway.
          const opens = off.length > 0 || Boolean(holiday)
          const Cell = opens ? 'button' : 'div'
          const body = (
            <>
              <span className="cal-date">{Number(d.slice(8))}</span>
              {/* A stat the company has said it works is still drawn, struck through: it
                  is the only place the day says what it was, and so the only way back to
                  marking it. */}
              {holiday && (
                <span
                  className={`cal-stat ${holiday.observed ? '' : 'dismissed'}`}
                  title={holiday.observed ? holiday.name : `${holiday.name} — worked here`}
                >
                  <Icon name="holiday" size={11} strokeWidth={2} />
                  <span className="cal-stat-name">{holiday.name}</span>
                </span>
              )}
              {off.map((t) => (
                <span
                  key={t.id}
                  className="cal-off"
                  title={[staffById.get(t.staffId)?.name, windowLabel(t, d), t.note].filter(Boolean).join(' · ')}
                >
                  {staffById.get(t.staffId)?.name || 'Someone'}
                  {windowLabel(t, d) && <span className="cal-off-hours"> {windowLabel(t, d)}</span>}
                </span>
              ))}
            </>
          )
          return (
            <Cell
              key={d}
              {...(opens
                ? { type: 'button', onClick: () => onPickDay(d), title: 'Open this day' }
                : {})}
              className={[
                'cal-day',
                outside ? 'outside' : '',
                weekend ? 'weekend' : '',
                holiday?.observed ? 'holiday' : '',
                isToday ? 'today' : '',
              ].filter(Boolean).join(' ')}
            >
              {body}
            </Cell>
          )
        })}
      </div>

      {timeOff.length === 0 && (
        <p className="hint" style={{ marginTop: 14 }}>Nobody is booked off this month.</p>
      )}
    </div>
  )
}
